/**
 * The ErrorBoundary of the entity route modules (web plan W4.4): what a
 * thrown 404 / 410 / 503 (src/seo/entityDocument.ts) or a failed render
 * shows, inside the site shell (src/routes/shell.tsx: header and footer stay).
 *
 * - 404 / 410: the root's "{Type} not found" head is the route's `meta`
 *   (`entityMeta`: its title, `noindex`, no canonical), and its H1 is the
 *   heading here, as the SPA's not-found state named it;
 * - anything else (503, 500), or without that head: the root ErrorBoundary's
 *   words, with its own `<title>` and `noindex` in JSX (React puts them in the
 *   head), never the page's canonical.
 */
import { isRouteErrorResponse, useRouteError, useRouteLoaderData } from 'react-router';
import { useCatalogPath } from '@/hooks/useRouteLocale';
import { useServesDocumentHead } from '@/hooks/useServesDocumentHead';
import { useTranslate } from '@/i18n/LocaleLanguage';
import type { DocumentData } from '@/seo/documentHead';
import { rootNotFoundHead } from '@/seo/entityDocument';
import { SITE_NAME } from '@/seo/templates';

export function RouteErrorBoundary() {
  const error = useRouteError();
  const status = isRouteErrorResponse(error) ? error.status : 500;
  const t = useTranslate();
  const catalogPath = useCatalogPath();
  const root = useRouteLoaderData('root') as DocumentData | undefined;
  const servedUrl = useServesDocumentHead();
  const notFound = status === 404 || status === 410;
  // The head `entityMeta` renders for a 404 / 410 on the URL it was served for: its title and robots are already there.
  const head = notFound && servedUrl ? rootNotFoundHead(root) : null;
  const [heading, text] = notFound
    ? [head?.h1 ?? t('common:document.error.notFound'), '']
    : status === 503
      ? [t('common:document.error.unavailable'), t('common:document.error.unavailableText')]
      : [t('common:document.error.failed'), t('common:document.error.failedText')];
  return (
    <div className="mx-auto flex min-h-page max-w-xl flex-col items-center justify-center gap-4 px-6 text-center">
      {head ? null : (
        <>
          <title>{`${heading} | ${SITE_NAME}`}</title>
          <meta name="robots" content="noindex" />
        </>
      )}
      <h1 className="text-2xl font-bold text-ink">{heading}</h1>
      {text ? <p className="text-sm text-ink-secondary">{text}</p> : null}
      <a className="font-semibold text-primary-strong underline" href={catalogPath('/')}>
        {t('common:document.error.home')}
      </a>
    </div>
  );
}

export default RouteErrorBoundary;
